/* Icons */
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import ReplayCircleFilledIcon from '@mui/icons-material/ReplayCircleFilled';

import { FC, useEffect, useMemo, useState, SetStateAction, Dispatch, useCallback } from 'react';
import { IconButton, MenuItem, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SplitButton } from '../../../../../../shared/components/splitButton/splitButton.component.tsx';
import { Radio } from '../../../../../../shared/components/form';
import { styled } from '@mui/material/styles';
import {
  CreationType,
  CreationWarnings,
  initOrderCreationState,
  IOrderCreation,
  orderCreationState,
} from '../../../states/orderDetails/orderCreation.state.ts';
import { snackbarService } from '../../../../../../shared/components/snackbar/service/snackbar.service.ts';
import { WarningsOnCreationPopup } from '../../../../../../shared/components/popupTemplates/warningsOnCreation.popup.tsx';
import { localeFormatterHelper } from '../../../../../../shared/helpers/formatter/localeFormatter.helper.ts';
import {
  CreateInvoiceRes,
  createInvoiceService,
} from '../../../../../invoice/services/createInvoice.service.ts';
import { customerListState, ICustomerListState } from '../../../states/customerList.state.ts';
import {
  C_Order_Error_Code,
  Order_Offer_Warning_Types,
  Order_Offer_Warning_Type_Fields,
} from '../../../../../../graphql/generatedModel.ts';
import { responseHandler } from '../../../../../../shared/responseHandler/responseHandler.ts';
import { Table } from '../../../../../../shared/components/table/table.component.tsx';
import { Column } from '../../../../../../shared/components/table/components/common.components.tsx';
import { GridColDef } from '@mui/x-data-grid-pro';

export const OrderActions: FC<IOrderActions> = ({ selectedCustomer, setIsEmptyTourError }) => {
  const [creationState, setCreationState] = useState<IOrderCreation>(initOrderCreationState);
  const [loading, setLoading] = useState<boolean>(false);
  const [noOrderBtnDisabled, setNoOrderBtnDisabled] = useState(false);
  const confirmCreationModal = useMemo(() => new WarningsOnCreationPopup(), []);
  const { t } = useTranslation();

  const handleNoOrder = () => {
    customerListState.pub.changeNoOrderStatus();
  };
  useEffect(() => {
    snackbarService.sub.show().subscribe(({ params: { id } }) => {
      setNoOrderBtnDisabled(id === 'noOrderStatusLoading');
    });
  }, []);

  const generateWarningTable = useCallback(
    (linkedRecords: IOrderCreation['linkedRecords']) => {
      if (linkedRecords) {
        const linkedRecordsArr = Object.keys(linkedRecords);
        const warningKey = linkedRecordsArr[0] as keyof typeof Order_Offer_Warning_Types;
        let enabledFields: Array<string> = [];
        const warningValues = linkedRecords[warningKey];
        if (warningKey && warningValues) {
          enabledFields = Object.keys(warningValues[0]);
          if (enabledFields.length) {
            return (
              <Table data={warningValues.map((el, i) => ({ ...el, id: i }))} height={210}>
                {enabledFields.map((field, idx) => {
                  const props: GridColDef = {
                    field,
                  };
                  switch (field) {
                    case Order_Offer_Warning_Type_Fields.ARTICLE:
                      props.flex = 1;
                      break;
                    case Order_Offer_Warning_Type_Fields.DAYS_TO_PRODUCE:
                      props.align = 'right';
                      props.headerAlign = 'right';
                      props.width = 180;
                      break;
                    case Order_Offer_Warning_Type_Fields.QUANTITY_PER_LOT:
                      props.align = 'right';
                      props.headerAlign = 'right';
                      props.width = 150;
                      break;
                    case Order_Offer_Warning_Type_Fields.QUANTITY:
                    case Order_Offer_Warning_Type_Fields.QUANTITY_MAX:
                    case Order_Offer_Warning_Type_Fields.QUANTITY_MIN:
                      props.align = 'right';
                      props.headerAlign = 'right';
                      props.width = 100;
                      break;
                    case Order_Offer_Warning_Type_Fields.AMOUNT_AFTER_SPLITTING:
                      props.align = 'right';
                      props.headerAlign = 'right';
                      props.width = 200;
                      break;
                  }

                  return (
                    <Column
                      headerName={t(`enums.${field}`)}
                      {...props}
                      sortable={false}
                      key={idx}
                    />
                  );
                })}
              </Table>
            );
          }
        }
      }
      return undefined;
    },
    [t],
  );

  useEffect(() => {
    const orderCreationSub = orderCreationState.sub.state().subscribe((v) => {
      setCreationState(v);
      const { warnings, existingOrdersOnDate, linkedRecords } = v;
      const warningTableComp = generateWarningTable(linkedRecords);
      if (warnings !== C_Order_Error_Code.OEC7_TOUR_IS_EMPTY_BUT_NEEDED) {
        setIsEmptyTourError(false);
      }
      switch (warnings) {
        case C_Order_Error_Code.OEC7_TOUR_IS_EMPTY_BUT_NEEDED: {
          setIsEmptyTourError(true);
          snackbarService.pub.show({
            type: 'error',
            content: t('order.tour_is_empty_but_needed'),
          });
          break;
        }
        case C_Order_Error_Code.OEC2_EMPTY_POSITIONS: {
          snackbarService.pub.show({
            type: 'error',
            content: t('order.empty_positions_create_warning'),
          });
          break;
        }
        case CreationWarnings['existingOrders']: {
          if (existingOrdersOnDate.length) {
            const content = (
              <>
                <p>{t('order.existing_orders_warning')}</p>
                <ul>
                  {existingOrdersOnDate.map((item) => {
                    if (!item) return null;
                    const { orderNo, userName, createDateTime } = item;
                    return (
                      <li key={orderNo}>
                        {t('common.delivery_no')} {orderNo} {userName} (
                        {localeFormatterHelper.formatDate(createDateTime)})
                      </li>
                    );
                  })}
                </ul>
              </>
            );
            confirmCreationModal.stream.emit('open', {
              title: t('order.create_order'),
              content,
              width: 700,
              onConfirm: () =>
                orderCreationState.pub.createOrder({
                  ignoreMinMaxProblems: false,
                  ignoreQuantityPerLotProblems: false,
                  ignoreSplittingProblems: false,
                  ignoreDaysToProduce: false,
                }),
            });
          }
          break;
        }
        case C_Order_Error_Code.OEC6_NOT_ENOUGH_DAYS_TO_PRODUCE: {
          const content = (
            <>
              <Typography sx={{ pb: 2 }}>{t('order.not_enough_days_to_produce')}</Typography>
              {warningTableComp}
            </>
          );
          confirmCreationModal.stream.emit('open', {
            title: t('order.create_order'),
            content,
            width: 700,
            onConfirm: () => {
              orderCreationState.pub.createOrder({
                ignoreDaysToProduce: true,
                ignoreQuantityPerLotProblems: false,
                ignoreMinMaxProblems: false,
                ignoreSplittingProblems: false,
              });
            },
          });
          break;
        }
        case C_Order_Error_Code.OEC3_QUANTITY_PER_LOT: {
          const content = (
            <>
              <Typography sx={{ pb: 2 }}>{t('order.positions_lot_quantity_warning')}</Typography>
              {warningTableComp}
            </>
          );
          confirmCreationModal.stream.emit('open', {
            title: t('order.create_order'),
            content,
            width: 700,
            onConfirm: () => {
              orderCreationState.pub.createOrder({
                ignoreDaysToProduce: true,
                ignoreQuantityPerLotProblems: true,
                ignoreMinMaxProblems: false,
                ignoreSplittingProblems: false,
              });
            },
          });
          break;
        }
        case C_Order_Error_Code.OEC4_MIN_MAX: {
          const content = (
            <>
              <Typography sx={{ pb: 2 }}>
                {t('order.positions_min_max_quantity_warning')}
              </Typography>
              {warningTableComp}
            </>
          );
          confirmCreationModal.stream.emit('open', {
            title: t('order.create_order'),
            content,
            width: 700,
            onConfirm: () => {
              orderCreationState.pub.createOrder({
                ignoreDaysToProduce: true,
                ignoreQuantityPerLotProblems: true,
                ignoreMinMaxProblems: true,
                ignoreSplittingProblems: false,
              });
            },
          });
          break;
        }
        case C_Order_Error_Code.OEC5_SPLITTING: {
          const content = (
            <>
              <Typography sx={{ pb: 2 }}>
                {t('order.delivery_splitting_calculations_warning')}
              </Typography>
              {warningTableComp}
            </>
          );
          confirmCreationModal.stream.emit('open', {
            title: t('order.create_order'),
            content,
            width: 700,
            onConfirm: () => {
              orderCreationState.pub.createOrder({
                ignoreDaysToProduce: true,
                ignoreQuantityPerLotProblems: true,
                ignoreMinMaxProblems: true,
                ignoreSplittingProblems: true,
              });
            },
          });
          break;
        }
        case CreationWarnings['unknown']: {
          snackbarService.pub.show({
            type: 'error',
            content: t('common.server_error'),
          });
          break;
        }
        default:
      }
    });

    const orderCreatedSub = orderCreationState.sub
      .createdOrder()
      .subscribe(({ createdOrder /*, creationSettings*/ }) => {
        if (createdOrder?.orderNo) {
          snackbarService.pub.show({
            type: 'success',
            content: `${t('common.delivery_no')} ${createdOrder.orderNo}`,
          });
        }
      });

    const invoiceCreatedSub = createInvoiceService.sub
      .createInvoice()
      .pipe(
        responseHandler<CreateInvoiceRes>({
          customErrorHandler: () => {
            return 'invoice.invoice_not_saved';
          },
          errorReturnType: undefined,
        }),
      )
      .subscribe((res) => {
        if (res?.invoiceNo) {
          snackbarService.pub.show({
            type: 'success',
            content: `${t('invoice.invoice_no')} ${res.invoiceNo}`,
          });
        }
        createInvoiceService.pub.clearStream();
      });

    const loadingSub = orderCreationState.sub.loading().subscribe((isLoading) => {
      setLoading(isLoading);
      if (isLoading) {
        snackbarService.pub.show({
          id: 'orderCreateLoading',
          type: 'loading',
          content: t('order.create_order'),
          noAutoHide: true,
        });
      } else {
        snackbarService.pub.hide('orderCreateLoading');
      }
    });

    return () => {
      orderCreationSub.unsubscribe();
      orderCreatedSub.unsubscribe();
      invoiceCreatedSub.unsubscribe();
      loadingSub.unsubscribe();
      confirmCreationModal.stream.unsubscribe();
      orderCreationState.pub.resetState();
    };
  }, []);

  const creationTitle = useMemo(() => {
    switch (creationState.creationSettings.type) {
      case 'withInvoice':
        return t('order.create_with_invoice');
      case 'payByCash':
        return t('order.pay_by_cash');
      default:
        return t('order.create_order');
    }
  }, [creationState.creationSettings.type, t]);

  return (
    <Stack
      direction='row'
      spacing={2}
      sx={{
        alignItems: 'center',
      }}
    >
      <IconButton
        onClick={handleNoOrder}
        disabled={noOrderBtnDisabled}
        color={selectedCustomer?.isNoOrderOnDate ? 'info' : 'error'}
        size='large'
        title={selectedCustomer?.isNoOrderOnDate ? t('order.restore') : t('order.no_order')}
      >
        {selectedCustomer?.isNoOrderOnDate ? <ReplayCircleFilledIcon /> : <NotInterestedIcon />}
      </IconButton>
      <SplitButton
        title={creationTitle}
        onClick={() => orderCreationState.pub.initCreateOrder()}
        disabled={loading}
        mainButtonDataAttr='create-order-button'
      >
        <SMenuItem>
          <Radio
            label={t('order.create_with_invoice')}
            value={'withInvoice'}
            checked={creationState.creationSettings.type === 'withInvoice'}
            onChange={(e) =>
              orderCreationState.pub.setCreationOption({ type: e.target.value as CreationType })
            }
            onClick={() =>
              creationState.creationSettings.type === 'withInvoice'
                ? orderCreationState.pub.setCreationOption({ type: undefined })
                : null
            }
          />
        </SMenuItem>
        <SMenuItem>
          <Radio
            label={t('order.pay_by_cash')}
            value={'payByCash'}
            checked={creationState.creationSettings.type === 'payByCash'}
            onChange={(e) =>
              orderCreationState.pub.setCreationOption({ type: e.target.value as CreationType })
            }
            onClick={() =>
              creationState.creationSettings.type === 'payByCash'
                ? orderCreationState.pub.setCreationOption({ type: undefined })
                : null
            }
          />
        </SMenuItem>
      </SplitButton>
      <confirmCreationModal.Component />
    </Stack>
  );
};

const SMenuItem = styled(MenuItem)({
  '& label > span': {
    paddingTop: 0,
    paddingBottom: 0,
  },
  '& label': {
    width: '100%',
    paddingTop: 2,
    paddingBottom: 2,
  },
});

interface IOrderActions {
  selectedCustomer?: ICustomerListState['selectedCustomer'];
  setIsEmptyTourError: Dispatch<SetStateAction<boolean>>;
}
